import { useState } from 'react'
import { useCourses } from '../../hooks/useApi'
import { Icon } from '@iconify/react'
import { Search, X } from 'lucide-react'
import type { CourseInfo } from '../../types/api'
import InteractiveCategoryCard from '../../components/CategoryGrid/InteractiveCategoryCard'
import '../../components/CategoryGrid/CategoryGrid.css'
import './CoursesPage.css'

const CoursesPage = () => {
  const { data, isLoading, error } = useCourses()
  const [searchQuery, setSearchQuery] = useState('')

  if (isLoading) {
    return (
      <div className="courses-page">
        <div className="courses-loading">
          <div className="courses-loading-spinner" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="courses-page">
        <div className="courses-placeholder">
          <Icon
            icon="solar:danger-triangle-linear"
            width={48}
            height={48}
            className="courses-placeholder-icon"
          />
          <h2 className="courses-placeholder-title">Error</h2>
          <p className="courses-placeholder-text">
            Couldn’t load courses. Please try again later.
          </p>
        </div>
      </div>
    )
  }

  const courses = data?.courses || []

  if (courses.length === 0) {
    return (
      <div className="courses-page">
        <div className="courses-placeholder">
          <Icon
            icon="solar:notebook-minimalistic-linear"
            width={48}
            height={48}
            className="courses-placeholder-icon"
          />
          <h2 className="courses-placeholder-title">Coming soon</h2>
          <p className="courses-placeholder-text">
            This section is under development. New courses are on the way.
          </p>
        </div>
      </div>
    )
  }

  // Фильтрация курсов по поисковому запросу
  const filterBySearch = (coursesList: CourseInfo[]) => {
    if (!searchQuery.trim()) return coursesList
    const query = searchQuery.toLowerCase()
    return coursesList.filter((c) =>
      c.base.name.toLowerCase().includes(query)
    )
  }

  // EU-сервис: только покупаемые курсы. Подписочных («мини-курсы») нет.
  const buyCourses = filterBySearch(
    courses.filter((c: CourseInfo) => c.base.grant_type === 'buy')
  )

  return (
    <div className="courses-page">
      {/* Поиск */}
      <div className="courses-search">
        <div className="courses-search-container">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search courses"
            className="courses-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="courses-search-clear"
              aria-label="Clear search"
            >
              <X size={18} />
            </button>
          )}
          <div className="courses-search-icon">
            <Search size={20} />
          </div>
        </div>
      </div>

      {/* Каталог курсов */}
      {buyCourses.length > 0 && (
        <div className="courses-section">
          <div className="courses-scroll">
            {buyCourses.map((course: CourseInfo) => {
              const hasTrial = (course.base.trial_lesson_count ?? 0) > 0
              return (
                <InteractiveCategoryCard
                  key={course.base.course_id}
                  item={course}
                  showingType="courses"
                  className="category-card course-card-large"
                  showLockBadge={!course.access && !hasTrial}
                  showDemoBadge={!course.access && hasTrial}
                  searchQuery={searchQuery}
                />
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default CoursesPage
